import api from "../middlware/api";
import { BASE_URL } from "../constants";
import { camelToSnake } from "../utils/CamelToSnake";

const PAYOUT_BASE = `${BASE_URL}/api/v1/payout-runs`;

function buildQuery(params: Record<string, any>): string {
  const query = new URLSearchParams();
  for (const key in params) {
    if (params[key] !== undefined && params[key] !== null && params[key] !== "") {
      query.set(key, String(params[key]));
    }
  }
  const qs = query.toString();
  return qs ? `?${qs}` : "";
}

/** Payout run status */
export type PayoutRunStatus = "draft" | "finalized" | "paid" | "cancelled";

/** Payout type */
export type PayoutType = "doctor" | "referral" | "staffIncentive";

export interface PayoutRun {
  id: number;
  runNumber?: string;
  clinicId: number;
  payoutType: PayoutType;
  totalPayoutAmount?: number;
  payeeCount?: number;
  periodStart: string;
  periodEnd: string;
  status: PayoutRunStatus;
  generatedBy?: number;
  generatedAt?: string;
  finalizedAt?: string;
  paidAt?: string;
  notes?: string;
  summary?: Record<string, any>;
}

/** List payout runs (optional filters: payoutType, status, periodStart, periodEnd, clinicId) */
export const getPayoutRuns = (params?: Record<string, any>) => {
  return api.get(`${PAYOUT_BASE}${buildQuery(params || {})}`);
};

/** Get single payout run */
export const getPayoutRun = (id: number | string) => {
  return api.get(`${PAYOUT_BASE}/${id}`);
};

/** Create payout run (draft). Payload: payoutType, periodStart, periodEnd, clinicId?, notes? */
export const createPayoutRun = (payload: Record<string, any>) => {
  return api.post(PAYOUT_BASE, camelToSnake(payload));
};

/** Update payout run (e.g. status, notes, summary) */
export const updatePayoutRun = (id: number | string, payload: Record<string, any>) => {
  return api.put(`${PAYOUT_BASE}/${id}`, camelToSnake(payload));
};

/** Rerun a draft payout run — deletes existing lines and regenerates them */
export const rerunPayoutRun = (id: number | string) => {
  return api.post(`${PAYOUT_BASE}/${id}/rerun`, {});
};

export interface PayoutLine {
  id: number;
  payoutRunId: number;
  payeeId: number;
  sourceType?: string;
  sourceId?: number | null;
  serviceDate?: string | null;
  visitId?: number | null;
  patientId?: number | null;
  baseAmount: number;
  sharePercent?: number | null;
  payoutAmount: number;
  calcMetaJson?: Record<string, any> | null;
  createdAt?: string;
  /** From API when visit/patient are loaded */
  patientName?: string | null;
  visitDate?: string | null;
  visitNumber?: string | number | null;
}

export interface PayoutPayment {
  id: number;
  payoutRunId: number;
  payeeId: number;
  status: string;
  paidAmount: number;
  paidAt?: string | null;
  mode?: string | null;
  reference?: string | null;
  notes?: string | null;
  createdAt?: string;
}

/** Get lines for a payout run. Backend expects payout_run_id in query. */
export const getPayoutLines = (payoutRunId: number | string, params?: Record<string, any>) => {
  const query: Record<string, any> = { payout_run_id: payoutRunId, ...params };
  return api.get(`${BASE_URL}/api/v1/payout-lines${buildQuery(query)}`);
};

/** Update a payout line (payout_amount). Only allowed when run is draft. */
export const updatePayoutLine = (id: number | string, payload: { payoutAmount: number }) => {
  return api.put(`${BASE_URL}/api/v1/payout-lines/${id}`, camelToSnake(payload));
};

/** Get payments for a payout run. Backend expects payout_run_id in query. */
export const getPayoutPayments = (payoutRunId: number | string, params?: Record<string, any>) => {
  const query: Record<string, any> = { payout_run_id: payoutRunId, ...params };
  return api.get(`${BASE_URL}/api/v1/payout-payments${buildQuery(query)}`);
};

/** Payload for recording payout payment(s). */
export interface RecordPayoutPaymentsPayload {
  payoutRunId: number;
  payeeIds?: number[];
  paidAt?: string;
  mode?: string;
  reference?: string;
  notes?: string;
  sendNotification?: boolean;
}

/** Record payment(s) for a run. Pass payeeIds to record for selected payees; omit to record for entire run. */
export const recordPayoutPayments = (payload: RecordPayoutPaymentsPayload) => {
  return api.post(`${BASE_URL}/api/v1/payout-payments`, camelToSnake(payload));
};
